import type { ReviewFinding } from '../types';
import type {
  FindingClassification,
  FindingClassificationMode,
  QuestionsSchemaWithFindingClassification,
} from '../types/reviewFindings';

export const OFF_FINDING_CLASSIFICATION: FindingClassification = { mode: 'off' };

const normalizedQuestionIds = (questionIds?: string[]): string[] =>
  Array.from(new Set((questionIds || []).filter(Boolean))).sort();

export const resolveFindingClassification = (
  schema: QuestionsSchemaWithFindingClassification | null | undefined,
  reviewCreatedAt?: Date | string | null
): FindingClassification => {
  const configured = schema?.findingClassification;
  if (!configured) {
    return OFF_FINDING_CLASSIFICATION;
  }

  const reviewTime = reviewCreatedAt ? new Date(reviewCreatedAt).getTime() : Date.now();
  const revisions = (configured.revisions || [])
    .filter(revision => Number.isFinite(new Date(revision.effectiveFrom).getTime()))
    .filter(revision => new Date(revision.effectiveFrom).getTime() <= reviewTime)
    .sort((left, right) =>
      new Date(left.effectiveFrom).getTime() - new Date(right.effectiveFrom).getTime()
    );

  const effective = revisions[revisions.length - 1];
  if (effective) {
    return {
      ...configured,
      mode: effective.mode,
      questionIds: normalizedQuestionIds(effective.questionIds),
      effectiveFrom: effective.effectiveFrom,
    };
  }

  if (configured.effectiveFrom) {
    const configuredTime = new Date(configured.effectiveFrom).getTime();
    if (!Number.isFinite(configuredTime) || configuredTime > reviewTime) {
      return OFF_FINDING_CLASSIFICATION;
    }
  }

  return {
    ...configured,
    questionIds: normalizedQuestionIds(configured.questionIds),
  };
};

export const findingClassificationForAdminSave = (
  current: FindingClassification | undefined,
  mode: FindingClassificationMode,
  questionIds: string[]
): FindingClassification => {
  const nextQuestionIds = mode === 'questions' ? normalizedQuestionIds(questionIds) : [];
  const currentQuestionIds = current?.mode === 'questions'
    ? normalizedQuestionIds(current.questionIds)
    : [];
  if (
    current &&
    current.mode === mode &&
    JSON.stringify(currentQuestionIds) === JSON.stringify(nextQuestionIds)
  ) {
    return current;
  }

  // The backend owns effectiveFrom and append-only revision history. Sending a
  // minimal changed policy avoids device-clock skew and prevents ordinary item
  // edits from manufacturing redundant revisions.
  return {
    mode,
    ...(mode === 'questions' ? { questionIds: nextQuestionIds } : {}),
  };
};

export const getFinding = (
  findings: ReviewFinding[] | undefined,
  questionId: string | null
): ReviewFinding | undefined =>
  (findings || []).find(finding => finding.questionId === questionId);

export const replaceFinding = (
  findings: ReviewFinding[] | undefined,
  questionId: string | null,
  finding: ReviewFinding | null
): ReviewFinding[] => {
  const remaining = (findings || []).filter(candidate => candidate.questionId !== questionId);
  return finding ? [...remaining, finding] : remaining;
};
